import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import type { CashflowSummary, Currency } from "../../types";
import { formatCurrency } from "../cashflow/CashflowContext";
import { formatPctSigned, lastTwoMonthMetrics } from "./dashboardUtils";
import { KpiCard } from "../KpiCard";

type Props = {
  summary: CashflowSummary | undefined;
  /** All-time (unscoped) summary for Current Balance only; independent of dashboard date filter. */
  balanceSummary: CashflowSummary | undefined;
  balanceSummaryLoading: boolean;
  currency: Currency;
  isLoading: boolean;
};

export function DashboardKpiStrip({
  summary,
  balanceSummary,
  balanceSummaryLoading,
  currency,
  isLoading,
}: Props) {
  const m = lastTwoMonthMetrics(summary);
  const balanceM = lastTwoMonthMetrics(balanceSummary);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[131px] animate-pulse rounded-xl border border-slate-200 bg-white"
          />
        ))}
      </div>
    );
  }

  const balanceEnd = balanceM.balanceEnd ?? 0;
  const incomePct = m.incomeVsPriorPct;
  const expensePct = m.expenseVsPriorPct;
  const balancePct = balanceM.balanceMomPct;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <KpiCard
        title="Current Balance"
        Icon={DollarSign}
        iconBgClass="bg-blue-50"
        iconColorClass="text-blue-600"
        value={
          balanceSummaryLoading ? "" : formatCurrency(balanceEnd, currency)
        }
        hint={
          balanceSummaryLoading
            ? "Loading balance…"
            : balancePct !== null
              ? `${formatPctSigned(balancePct)} from last month`
              : "Ending balance (latest month)"
        }
        valueClassName={
          balanceSummaryLoading
            ? "mt-3 block h-8 max-w-[12rem] animate-pulse rounded-md bg-slate-200"
            : "mt-3 text-2xl font-semibold tabular-nums tracking-tight text-slate-900"
        }
        hintClassName="mt-1 text-xs leading-snug text-slate-500"
        titleClassName="text-xs font-medium uppercase tracking-wide text-slate-500"
      />
      <KpiCard
        title="Total Cash In"
        Icon={TrendingUp}
        iconBgClass="bg-green-50"
        iconColorClass="text-green-600"
        value={formatCurrency(summary?.totalIncome ?? 0, currency)}
        hint={
          incomePct !== null
            ? `${formatPctSigned(incomePct)} vs prior period`
            : "This period"
        }
        hintClassName="mt-1 text-xs leading-snug text-emerald-600"
        titleClassName="text-xs font-medium uppercase tracking-wide text-slate-500"
        valueClassName="mt-3 text-2xl font-semibold tabular-nums tracking-tight text-slate-900"
      />
      <KpiCard
        title="Total Cash Out"
        Icon={TrendingDown}
        iconBgClass="bg-red-50"
        iconColorClass="text-red-600"
        value={formatCurrency(summary?.totalExpenses ?? 0, currency)}
        hint={
          expensePct !== null
            ? `${formatPctSigned(expensePct)} vs prior period`
            : "This period"
        }
        hintClassName="mt-1 text-xs leading-snug text-rose-600"
        titleClassName="text-xs font-medium uppercase tracking-wide text-slate-500"
        valueClassName="mt-3 text-2xl font-semibold tabular-nums tracking-tight text-slate-900"
      />
    </div>
  );
}
